<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import { UploadStatus, useGalleryUploadManager } from '@/composables/useGalleryUploadManager'
import type { UploadEntry } from '@/composables/useGalleryUploadManager'

const { t } = useI18n()
const manager = useGalleryUploadManager()
const { entries, activeCount, failedCount, doneCount, totalCount } = manager

const expanded = ref(false)
const announcement = ref('')

const visibleEntries = computed(() => entries.value.filter((e) => e.status !== UploadStatus.Cancelled))

interface EntryGroup {
  key: string
  to: string
  entries: UploadEntry[]
}

const groups = computed<EntryGroup[]>(() => {
  const map = new Map<string, EntryGroup>()
  for (const entry of visibleEntries.value) {
    const key = `${entry.eventSlug}|${entry.galleryId}`
    const group = map.get(key) ?? {
      key,
      to: `/organizer/events/${entry.eventSlug}/galleries/${entry.galleryId}`,
      entries: [],
    }
    group.entries.push(entry)
    map.set(key, group)
  }
  return [...map.values()]
})

const summary = computed(() => {
  const base =
    activeCount.value > 0
      ? t('galleries.tray.summary', { done: doneCount.value, total: totalCount.value })
      : t('galleries.tray.finished', { done: doneCount.value, total: totalCount.value })
  return failedCount.value > 0 ? `${base} - ${t('galleries.tray.failed', failedCount.value)}` : base
})

const hasFinished = computed(() => entries.value.some((e) => (
  e.status === UploadStatus.Ready || e.status === UploadStatus.Failed || e.status === UploadStatus.Cancelled
)))

// Only batch milestones are announced to screen readers, not every progress tick.
watch(activeCount, (now, before) => {
  if (before > 0 && now === 0 && totalCount.value > 0) {
    announcement.value = t('galleries.tray.announce_done', {
      done: doneCount.value,
      total: totalCount.value,
      failed: failedCount.value,
    })
  }
})

function statusLabel(entry: UploadEntry): string {
  if (entry.status === UploadStatus.Uploading) {
    return t('galleries.tray.status.uploading', { percent: Math.round(entry.progress) })
  }
  if (entry.status === UploadStatus.Failed && entry.failure !== null) {
    return t(`galleries.reasons.${entry.failure.reason}`, { max: 50 })
  }
  return t(`galleries.tray.status.${entry.status}`)
}

function isBusy(entry: UploadEntry): boolean {
  return (
    entry.status === UploadStatus.Preparing ||
    entry.status === UploadStatus.Reserving ||
    entry.status === UploadStatus.Uploading ||
    entry.status === UploadStatus.Completing ||
    entry.status === UploadStatus.Processing
  )
}

function canCancel(entry: UploadEntry): boolean {
  return entry.status !== UploadStatus.Ready && entry.status !== UploadStatus.Failed && entry.status !== UploadStatus.Cancelled
}
</script>

<template>
  <div>
    <p class="sr-only" aria-live="polite" role="status">{{ announcement }}</p>

    <div
      v-if="visibleEntries.length > 0"
      class="fixed bottom-4 right-4 z-40 w-80 max-w-[calc(100vw-2rem)] rounded-2xl border border-slate-600 bg-slate-800 shadow-2xl"
      data-testid="gallery-upload-tray"
    >
      <div class="flex items-center gap-2 px-4 py-3">
        <AppSpinner v-if="activeCount > 0" size="sm" color="text-emerald-500" />
        <p class="flex-1 min-w-0 text-sm font-medium text-white truncate">{{ summary }}</p>
        <button
          type="button"
          class="text-xs font-semibold text-emerald-400 hover:text-emerald-300"
          :aria-expanded="expanded"
          @click="expanded = !expanded"
        >
          {{ expanded ? t('galleries.tray.hide') : t('galleries.tray.show') }}
        </button>
      </div>

      <div v-if="expanded" class="max-h-80 overflow-y-auto border-t border-slate-700 px-4 py-3 space-y-4">
        <section v-for="group in groups" :key="group.key">
          <RouterLink :to="group.to" class="text-xs font-semibold text-emerald-400 hover:text-emerald-300">
            {{ t('galleries.tray.open_gallery') }}
          </RouterLink>
          <ul class="mt-2 space-y-2">
            <li v-for="entry in group.entries" :key="entry.id" class="text-xs" :data-testid="`tray-entry-${entry.id}`">
              <div class="flex items-center gap-2">
                <span class="flex-1 truncate text-slate-200">{{ entry.fileName }}</span>
                <button
                  v-if="entry.status === UploadStatus.Failed && entry.failure?.retryable"
                  type="button"
                  class="text-slate-300 hover:text-white underline underline-offset-2"
                  @click="manager.retry(entry.id)"
                >
                  {{ t('galleries.tile.retry') }}
                </button>
                <button
                  v-if="canCancel(entry)"
                  type="button"
                  class="text-slate-300 hover:text-white underline underline-offset-2"
                  @click="manager.cancel(entry.id)"
                >
                  {{ t('galleries.tile.cancel') }}
                </button>
                <button
                  v-else
                  type="button"
                  class="text-slate-300 hover:text-white underline underline-offset-2"
                  @click="manager.remove(entry.id)"
                >
                  {{ t('galleries.tile.remove') }}
                </button>
              </div>
              <p class="mt-0.5" :class="entry.status === UploadStatus.Failed ? 'text-red-300' : 'text-slate-400'">
                <AppSpinner v-if="isBusy(entry)" size="sm" color="text-slate-400" />
                {{ statusLabel(entry) }}
              </p>
            </li>
          </ul>
        </section>
        <button
          v-if="hasFinished"
          type="button"
          class="text-xs font-medium text-slate-300 hover:text-white underline underline-offset-2"
          @click="manager.clearFinished()"
        >
          {{ t('galleries.tray.clear_finished') }}
        </button>
      </div>
    </div>
  </div>
</template>
